import React, { RefObject, useContext } from "react";
import { Column } from "react-table";
import { Plan, PlanDetailsPageSection } from "../../../@types";
import { col } from "../../../@types/react-table";
import { RenderedTable } from "../../../components";
import {
  planDataUtils,
  PreviewContext,
  useTableConfig,
  useTranslate,
} from "../../../helpers";
import { hasOpioidTreatmentBenefit } from "../../../helpers/benefitHelpers";
import { PlanDetailSection } from "../components";
import { dataAccessor } from "./dataAccesor";
import { DoctorServicesTableCaption } from "./DoctorServicesTableCaption";
import { PreventiveServicesTableCaption } from "./PreventiveServicesTableCaption";
import { ToggleMoreBenefits } from "../components/ToggleMoreBenefits";
import { shouldSuppressPlanBenefits } from "../../../helpers";
import { Alert } from "@cmsgov/ds-medicare-gov";

export const moreBenefitsAndCostsButtonTextKey =
  "plan_terms.benefits_and_costs.open";

const BenefitsAndCostsSection: React.FunctionComponent<{
  plan: Plan;
  sectionRef: RefObject<HTMLDivElement>;
}> = ({ plan, sectionRef }) => {
  const t = useTranslate();

  const previewMode = useContext(PreviewContext);
  const suppressed = shouldSuppressPlanBenefits(plan.redactions);

  const doctorServicesData = useTableConfig(
    dataAccessor.getDoctorServicesData(
      plan,
      t("plan_terms.doctor_services"),
      t
    ),
    [t]
  );

  const testingLabsAndImagingData = useTableConfig(
    dataAccessor.getTestsLabsAndImaging(
      plan,
      t("plan_terms.tests_labs_imaging"),
      t,
      previewMode
    ),
    [t]
  );

  const hospitalServicesData = useTableConfig(
    dataAccessor.getHospitalServices(
      plan,
      t("plan_terms.hospital_services"),
      t
    ),
    [t]
  );

  const skilledNursingFacilityData = useTableConfig(
    dataAccessor.getSkilledNursingFacility(
      plan,
      t("plan_terms.skilled_nursing"),
      t
    ),
    [t]
  );

  const preventiveServicesData = useTableConfig(
    dataAccessor.getPreventiveServices(
      plan,
      t("plan_terms.preventive_services"),
      t
    ),
    [t]
  );

  const ambulanceData = useTableConfig(
    dataAccessor.getAmbulance(plan, t("plan_terms.ambulance"), t),
    [t]
  );

  const therapyServicesData = useTableConfig(
    dataAccessor.getTherapyServices(plan, t("plan_terms.therapy_services"), t),
    [t]
  );

  const mentalHealthServicesData = useTableConfig(
    dataAccessor.getMentalHealthServices(
      plan,
      t("plan_terms.mental_health"),
      t
    ),
    [t]
  );

  const opioidTreatmentProgramServicesData = useTableConfig(
    dataAccessor.getOpioidTreatmentProgramServices(
      plan,
      t("more_benefits.opioid_treatment"),
      t,
      suppressed
    ),
    [t]
  );

  const otherServicesData = useTableConfig(
    dataAccessor.getOtherServices(
      plan,
      t("plan_terms.other_services"),
      t,
      suppressed
    ),
    [t]
  );

  const MMPBenefitsData = useTableConfig(
    dataAccessor.getMMPBenefits(
      plan,
      t("plan_terms.mmp_home_health_services"),
      t
    ),
    [t]
  );

  const defaultColumns: Array<Column<col>> = useTableConfig([
    {
      accessor: "key",
    },
    {
      accessor: "value",
    },
    {
      accessor: "limitsApply",
    },
  ]);

  return (
    <PlanDetailSection
      id={PlanDetailsPageSection.BENEFITS_AND_COSTS}
      title={t("plan_terms.benefits_and_costs")}
      sectionRef={sectionRef}
    >
      {shouldSuppressPlanBenefits(plan.redactions) ? (
        <div className="PlanDetailsSection__alert-container">
          <Alert
            variation="warn"
            heading={t("suppressed_plan_benefit.alert.title")}
            hideIcon
          >
            {t("suppressed_plan_benefit.alert.content")}
          </Alert>
        </div>
      ) : (
        <>
          <RenderedTable
            id="DoctorServicesTable"
            className="three-column-table"
            tableColumns={defaultColumns}
            tableData={doctorServicesData}
            caption={<DoctorServicesTableCaption plan={plan} />}
            borderless
            rowHeaders
            stackableBreakpoint="lg"
          />
          <RenderedTable
            id="TestingLabsAndImagingTable"
            className="three-column-table"
            tableColumns={defaultColumns}
            tableData={testingLabsAndImagingData}
            caption={t("plan_terms.tests_labs_imaging")}
            borderless
            rowHeaders
            stackableBreakpoint="lg"
          />
          <RenderedTable
            id="HospitalServicesTable"
            className="three-column-table"
            tableColumns={defaultColumns}
            tableData={hospitalServicesData}
            caption={t("plan_terms.hospital_services")}
            borderless
            rowHeaders
            stackableBreakpoint="lg"
          />
          <RenderedTable
            id="SkilledNursingFacilityTable"
            className="three-column-table"
            tableColumns={defaultColumns}
            tableData={skilledNursingFacilityData}
            caption={t("plan_terms.skilled_nursing")}
            borderless
            rowHeaders
            stackableBreakpoint="lg"
          />
          <RenderedTable
            id="PreventiveServicesTable"
            className="three-column-table"
            tableColumns={defaultColumns}
            tableData={preventiveServicesData}
            caption={<PreventiveServicesTableCaption />}
            borderless
            rowHeaders
            stackableBreakpoint="lg"
          />
          <RenderedTable
            id="AmbulanceTable"
            className="three-column-table"
            tableColumns={defaultColumns}
            tableData={ambulanceData}
            caption={t("plan_terms.ambulance")}
            borderless
            rowHeaders
            stackableBreakpoint="lg"
          />
          <RenderedTable
            id="TherapyServicesTable"
            className="three-column-table"
            tableColumns={defaultColumns}
            tableData={therapyServicesData}
            caption={t("plan_terms.therapy_services")}
            borderless
            rowHeaders
            stackableBreakpoint="lg"
          />
          <ToggleMoreBenefits
            buttonTextToOpen={t(moreBenefitsAndCostsButtonTextKey)}
            buttonTextToCollapse={t("plan_terms.benefits_and_costs.collapse")}
            section="benefits-and-costs"
          >
            <RenderedTable
              id="MentalHealthServicesTable"
              className="three-column-table"
              tableColumns={defaultColumns}
              tableData={mentalHealthServicesData}
              caption={t("plan_terms.mental_health")}
              borderless
              rowHeaders
              stackableBreakpoint="lg"
            />
            {hasOpioidTreatmentBenefit(plan) && (
              <RenderedTable
                id="OpioidTreatmentBenefitTable"
                className="three-column-table"
                tableColumns={defaultColumns}
                tableData={opioidTreatmentProgramServicesData}
                caption={t("more_benefits.opioid_treatment")}
                borderless
                rowHeaders
                stackableBreakpoint="lg"
              />
            )}
            <RenderedTable
              id="OtherServicesTable"
              className="three-column-table"
              tableColumns={defaultColumns}
              tableData={otherServicesData}
              caption={t("plan_terms.other_services")}
              borderless
              rowHeaders
              stackableBreakpoint="lg"
            />
            {planDataUtils.showMMPBenefits(plan) && (
              <RenderedTable
                id="MMPBenefitsTable"
                className="three-column-table"
                tableColumns={defaultColumns}
                tableData={MMPBenefitsData}
                caption={t("plan_terms.mmp_home_health_services")}
                borderless
                rowHeaders
                stackableBreakpoint="lg"
              />
            )}
          </ToggleMoreBenefits>
        </>
      )}
    </PlanDetailSection>
  );
};
export { BenefitsAndCostsSection };
